"use client";

import {
  PopoverAnchor,
  PopoverContent,
  type PopoverContentProps,
  PopoverRoot,
} from "@elion-health/ui";
import {
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  useState,
} from "react";

/** A router link that takes anchor props, e.g. `next/link`. */
export type HoverPopoverLink =
  | "a"
  | ComponentType<ComponentProps<"a"> & { href: string }>;

export interface HoverPopoverProps {
  /** Accessible name for the trigger; the panel itself is hidden from AT. */
  label: string;
  /** What the trigger shows. */
  trigger: ReactNode;
  triggerClassName?: string;
  onClick?: () => void;
  /** Makes the trigger a link to this destination instead of a button. */
  href?: string;
  linkComponent?: HoverPopoverLink;
  side?: PopoverContentProps["side"];
  align?: PopoverContentProps["align"];
  /** Classes for the panel, e.g. its width and padding. */
  className?: string;
  children: ReactNode;
}

const FOCUS =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)";

/**
 * A trigger that shows a small informational panel while it is hovered or
 * focused. The panel ignores the pointer, so moving off the trigger closes
 * it, and the trigger keeps focus. Put everything the panel says in `label`
 * too, since assistive tech never reads the panel.
 */
export function HoverPopover({
  label,
  trigger,
  triggerClassName = "",
  onClick,
  href,
  linkComponent: Link = "a",
  side = "bottom",
  align = "start",
  className = "",
  children,
}: HoverPopoverProps) {
  const [open, setOpen] = useState(false);
  const hover = {
    "aria-label": label,
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
  };
  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        {href ? (
          <Link
            href={href}
            {...hover}
            className={`cursor-pointer no-underline! ${FOCUS} ${triggerClassName}`}
          >
            {trigger}
          </Link>
        ) : (
          <button
            type="button"
            onClick={onClick}
            {...hover}
            className={`${FOCUS} ${
              onClick ? "cursor-pointer" : "cursor-default"
            } ${triggerClassName}`}
          >
            {trigger}
          </button>
        )}
      </PopoverAnchor>
      <PopoverContent
        side={side}
        align={align}
        aria-hidden={true}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        className={`pointer-events-none ${className}`}
      >
        {children}
      </PopoverContent>
    </PopoverRoot>
  );
}
